/**
 * InstallPrompt — shown when pwa.vicin.app is opened in a regular
 * browser tab instead of as an installed PWA. Uses the GameBoy LCD
 * aesthetic (pixel font, dashed dialog border, GB color palette)
 * but WITHOUT the plastic GameBoy shell — because the shell only
 * makes sense once the user is "inside" the device. This screen IS
 * the LCD, exposed.
 *
 * Includes per-platform install instructions + a bypass link for
 * developers (also accessible via ?pwa-bypass=1).
 */
import { useEffect, useState } from 'react';
import { useApp } from '@/state/store';
import { BellSprite, DoorSprite, KeySprite, MailSprite, WrenchSprite, GearSprite } from '@/components/Sprites';

type Platform = 'ios' | 'android' | 'desktop' | 'unknown';

function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'desktop';
}

export function InstallPrompt() {
  const { setMenuIndex, menuIndex, showToast } = useApp();
  const [platform, setPlatform] = useState<Platform>('unknown');
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);

  useEffect(() => {
    setPlatform(detectPlatform());

    // Android / Chrome desktop install prompt — listen for the browser's
    // beforeinstallprompt event so we can show a one-tap install button.
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const steps: Record<Platform, string[]> = {
    ios: [
      '1. TOCÁ EL ÍCONO "COMPARTIR" DE SAFARI',
      '   (el cuadrito con la flecha hacia arriba)',
      '',
      '2. ELEGÍ "AÑADIR A PANTALLA DE INICIO"',
      '',
      '3. TOCÁ "AÑADIR" EN LA ESQUINA',
      '',
      '4. ABRÍ VICIN DESDE EL ICONO NUEVO',
      '   (¡no desde Safari — desde el icono!)',
    ],
    android: [
      '1. TOCÁ EL MENÚ (3 PUNTOS) DEL NAVEGADOR',
      '',
      '2. ELEGÍ "INSTALAR APP" O "AÑADIR A INICIO"',
      '',
      '3. CONFIRMÁ LA INSTALACIÓN',
      '',
      '4. ABRÍ VICIN DESDE EL ICONO NUEVO',
    ],
    desktop: [
      '1. BUSCÁ EL ICONO "INSTALAR" EN LA BARRA',
      '   DE DIRECCIONES DEL NAVEGADOR (⊕ o 💻)',
      '',
      '2. CONFIRMÁ LA INSTALACIÓN',
      '',
      '3. ABRÍ VICIN COMO APP INSTALADA',
      '   (aparece en tu menú de aplicaciones)',
    ],
    unknown: [
      '1. ABRÍ ESTA URL DESDE EL NAVEGADOR',
      '   DE TU TELÉFONO',
      '',
      '2. SEGUID LAS INSTRUCCIONES DEL',
      '   NAVEGADOR PARA INSTALAR LA APP',
    ],
  };

  const stepText = steps[platform];

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      showToast('SEGUID LOS PASOS DE ABAJO', 'info');
      return;
    }
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice.outcome === 'accepted') {
      showToast('¡INSTALADA! YA PODÉS ABRIRLA', 'success');
    }
    setDeferredPrompt(null);
  };

  return (
    <div className="install-prompt">
      <div className="install-prompt__lcd">
        {/* Boot animation header */}
        <div className="install-prompt__status">
          <span className="install-prompt__status-dot" />
          <span className="gb-pix gb-pix-xs">VICIN · PWA</span>
        </div>

        {/* Title block */}
        <div className="install-prompt__title">
          <BellSprite size={40} />
          <div className="gb-pix" style={{ fontSize: 18, lineHeight: 1.3 }}>
            INSTALÁ VICIN
          </div>
          <div className="gb-pix gb-mid-text" style={{ fontSize: 9, lineHeight: 1.5, textAlign: 'center', maxWidth: 280 }}>
            PARA QUE EL TIMBRE SUENE Y LAS NOTIFICACIONES LLEGUEN, INSTALÁ
            LA APP EN TU DISPOSITIVO.
          </div>
        </div>

        {/* Why install — quick value props */}
        <div className="install-prompt__why">
          <div className="install-prompt__why-row">
            <BellSprite size={18} />
            <span className="gb-pix">TIMBRE EN PANTALLA BLOQUEADA</span>
          </div>
          <div className="install-prompt__why-row">
            <DoorSprite size={18} />
            <span className="gb-pix">ABRÍ LA PUERTA DESDE LA APP</span>
          </div>
          <div className="install-prompt__why-row">
            <MailSprite size={18} />
            <span className="gb-pix">AVISOS DE ANUNCIOS Y REPORTES</span>
          </div>
        </div>

        {/* Per-platform steps */}
        <div className="install-prompt__steps">
          <div className="install-prompt__steps-title gb-pix gb-mid-text">
            PASOS PARA {platform === 'ios' ? 'IPHONE/IPAD' : platform === 'android' ? 'ANDROID' : 'ESCRITORIO'}
          </div>
          <pre className="install-prompt__steps-text">
            {stepText.join('\n')}
          </pre>
        </div>

        {/* One-tap install (only on Android/desktop where the API exists) */}
        {deferredPrompt && (
          <button
            className="install-prompt__install-btn gb-pix"
            onClick={handleInstallClick}
            data-testid="install-one-tap"
          >
            [A] INSTALAR AHORA
          </button>
        )}

        {/* Bypass for developers */}
        <a
          href="?pwa-bypass=1"
          className="install-prompt__bypass gb-pix gb-mid-text"
          data-testid="install-bypass"
        >
          · MODO DESARROLLADOR: ENTRAR SIN INSTALAR ·
        </a>
      </div>
    </div>
  );
}